/**
 * Political campaign postcard TEMPLATES — pure CSS/SVG, no photos.
 *
 * Each template is a 5:9 landscape mailer side that renders LIVE from
 * personalization props so the buyer sees their own name/office/slogan
 * printed on a real postcard design (not a stock photo). Six distinct
 * looks; each adapts to party color (R = red, D = blue, else neutral navy).
 *
 * Usage:
 *   <PostcardPreview templateId="stars" side="front" data={{candidateName, office, slogan, party, paidFor}} />
 *   TEMPLATES  — array of {id, label} for the gallery
 */
import React from 'react';

// ---- palette ---------------------------------------------------------------
const RED = '#b22234';       // old-glory red
const BLUE = '#0a3161';      // old-glory blue
const CREAM = '#f4f1e8';
const GOLD = '#c8a24a';

function partyColors(party) {
  const p = (party || '').toUpperCase();
  if (p === 'R' || p === 'REP') return { primary: RED, secondary: BLUE, accent: '#fff' };
  if (p === 'D' || p === 'DEM') return { primary: BLUE, secondary: RED, accent: '#fff' };
  return { primary: BLUE, secondary: '#7c1d2b', accent: '#fff' };
}

// A crisp 5-point star as inline SVG (reused across templates).
const Star = ({ size = 16, color = '#fff', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden>
    <path fill={color} d="M12 2l2.9 6.26 6.9.6-5.2 4.55 1.55 6.74L12 17.27 5.85 20.7 7.4 13.96 2.2 9.41l6.9-.6z" />
  </svg>
);

const StarRow = ({ n = 5, size = 12, color = '#fff', gap = 6 }) => (
  <div style={{ display: 'flex', gap, justifyContent: 'center' }}>
    {Array.from({ length: n }).map((_, i) => <Star key={i} size={size} color={color} />)}
  </div>
);

// Base frame: fixed 9:5 aspect, rounded, print-bleed shadow. `scale` lets the
// same absolute-positioned type work at any thumbnail/preview size.
const Frame = ({ children, bg }) => (
  <div style={{
    position: 'relative', width: '100%', aspectRatio: '9 / 5', borderRadius: 10,
    overflow: 'hidden', boxShadow: '0 4px 18px rgba(0,0,0,.18)', background: bg,
    fontFamily: "'Oswald','Arial Narrow',sans-serif",
  }}>{children}</div>
);

const upper = { textTransform: 'uppercase', letterSpacing: '.04em' };

// Small helper for the legally-required disclaimer strip.
const PaidFor = ({ text, color = 'rgba(255,255,255,.85)' }) => (
  <div style={{ position: 'absolute', bottom: 5, left: 0, right: 0, textAlign: 'center', fontSize: '2.2cqw', color, letterSpacing: '.02em', fontFamily: 'Georgia, serif' }}>
    {text || 'Paid for by the Committee to Elect'}
  </div>
);

// cqw (container query width) keeps type proportional to the card at any size.
const cq = { containerType: 'inline-size' };

// ===========================================================================
// TEMPLATE 1 — "Stars & Stripes" (classic patriotic, front hero)
// ===========================================================================
const Stars = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg={CREAM}>
        <div style={{ ...cq, position: 'absolute', inset: 0 }}>
          <div style={{ height: '22%', background: c.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StarRow n={7} size={10} />
          </div>
          <div style={{ padding: '4cqw 6cqw', color: c.primary }}>
            <div style={{ ...upper, fontWeight: 700, fontSize: '6cqw', lineHeight: 1.05 }}>{d.slogan || 'Leadership that works for us'}</div>
            <div style={{ marginTop: '3cqw', fontSize: '3.4cqw', color: '#333', fontFamily: 'Georgia, serif', lineHeight: 1.35 }}>
              {d.message || 'Standing up for our families, our schools, and our future. Join us and make your voice heard this election.'}
            </div>
          </div>
          <div style={{ position: 'absolute', bottom: 0, height: '10%', width: '100%', background: `repeating-linear-gradient(90deg, ${c.secondary} 0 12%, ${CREAM} 12% 24%)` }} />
          <PaidFor text={d.paidFor} color="rgba(0,0,0,.55)" />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg={c.primary}>
      <div style={{ ...cq, position: 'absolute', inset: 0 }}>
        {/* canton of stars */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: '38%', height: '62%', background: c.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '2cqw' }}>
            {Array.from({ length: 9 }).map((_, i) => <Star key={i} size={14} color="#fff" />)}
          </div>
        </div>
        {/* stripes */}
        <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '38%', background: `repeating-linear-gradient(180deg, ${CREAM} 0 20%, ${c.secondary} 20% 40%)`, opacity: .9 }} />
        <div style={{ position: 'absolute', top: '8%', right: '5%', width: '58%', textAlign: 'right', color: '#fff' }}>
          <div style={{ ...upper, fontSize: '3.4cqw', letterSpacing: '.3em', opacity: .9 }}>Vote</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '11cqw', lineHeight: .95, textShadow: '0 2px 8px rgba(0,0,0,.3)' }}>{d.candidateName || 'Your Name'}</div>
          <div style={{ ...upper, fontSize: '3.6cqw', color: GOLD, fontWeight: 600, marginTop: '1cqw' }}>{d.office || 'for Office'}</div>
          {d.slogan && <div style={{ fontSize: '3cqw', color: '#fff', fontStyle: 'italic', fontFamily: 'Georgia, serif', marginTop: '1.5cqw' }}>{d.slogan}</div>}
        </div>
        <PaidFor text={d.paidFor} />
      </div>
    </Frame>
  );
};

// ===========================================================================
// TEMPLATE 2 — "Modern Civic" (clean, minimal, editorial)
// ===========================================================================
const Modern = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg="#fff">
        <div style={{ ...cq, position: 'absolute', inset: 0, padding: '6cqw' }}>
          <div style={{ width: '14%', height: '3cqw', background: c.primary, marginBottom: '4cqw' }} />
          <div style={{ ...upper, fontWeight: 700, fontSize: '6.5cqw', color: '#111', lineHeight: 1.05 }}>{d.slogan || 'A fresh vision for our community'}</div>
          <div style={{ marginTop: '3cqw', fontSize: '3.4cqw', color: '#555', lineHeight: 1.4, fontFamily: 'Georgia, serif' }}>
            {d.message || 'Real solutions on the issues that matter — good jobs, safe streets, and schools we can be proud of.'}
          </div>
          <div style={{ position: 'absolute', bottom: '8%', left: '6cqw', ...upper, fontSize: '3cqw', color: c.primary, fontWeight: 700 }}>{(d.candidateName || 'Your Name')} · {d.office || 'for Office'}</div>
          <PaidFor text={d.paidFor} color="rgba(0,0,0,.5)" />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg="#fff">
      <div style={{ ...cq, position: 'absolute', inset: 0 }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '46%', background: c.primary }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '46%', width: '3cqw', background: GOLD }} />
        <div style={{ position: 'absolute', left: 0, width: '46%', top: '50%', transform: 'translateY(-50%)', textAlign: 'center', color: '#fff', padding: '0 3cqw' }}>
          <StarRow n={3} size={14} />
          <div style={{ ...upper, fontWeight: 700, fontSize: '9cqw', lineHeight: .95, marginTop: '3cqw' }}>{d.candidateName || 'Your Name'}</div>
        </div>
        <div style={{ position: 'absolute', right: '4cqw', top: '50%', transform: 'translateY(-50%)', width: '46%', color: '#111' }}>
          <div style={{ ...upper, fontSize: '3cqw', letterSpacing: '.3em', color: c.primary, fontWeight: 700 }}>Elect</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '5.5cqw', lineHeight: 1.05, margin: '1cqw 0' }}>{d.office || 'for Office'}</div>
          <div style={{ fontSize: '3.4cqw', color: '#666', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{d.slogan || 'Leadership you can trust.'}</div>
        </div>
        <PaidFor text={d.paidFor} color="rgba(0,0,0,.45)" />
      </div>
    </Frame>
  );
};

// ===========================================================================
// TEMPLATE 3 — "Bold Type" (huge typography, maximal)
// ===========================================================================
const BoldType = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg={c.primary}>
        <div style={{ ...cq, position: 'absolute', inset: 0, padding: '6cqw', color: '#fff' }}>
          <div style={{ ...upper, fontWeight: 700, fontSize: '13cqw', lineHeight: .9, color: GOLD }}>{d.slogan ? d.slogan.split(' ')[0] : 'Forward'}</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '13cqw', lineHeight: .9 }}>{d.slogan ? d.slogan.split(' ').slice(1).join(' ') || 'Together' : 'Together'}</div>
          <div style={{ marginTop: '3cqw', fontSize: '3.4cqw', maxWidth: '80%', lineHeight: 1.4, fontFamily: 'Georgia, serif', opacity: .92 }}>
            {d.message || 'It is time for bold, honest leadership. Vote for change that puts our community first.'}
          </div>
          <PaidFor text={d.paidFor} />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg={CREAM}>
      <div style={{ ...cq, position: 'absolute', inset: 0 }}>
        <div style={{ position: 'absolute', top: 0, width: '100%', height: '14%', background: c.primary }} />
        <div style={{ position: 'absolute', bottom: 0, width: '100%', height: '14%', background: c.secondary }} />
        <div style={{ position: 'absolute', inset: '16% 6cqw', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ ...upper, fontSize: '4cqw', letterSpacing: '.4em', color: c.primary, fontWeight: 700 }}>★ Vote ★</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '15cqw', lineHeight: .85, color: '#111' }}>{d.candidateName || 'Your Name'}</div>
          <div style={{ ...upper, fontSize: '4.5cqw', fontWeight: 700, color: c.secondary, marginTop: '1cqw' }}>{d.office || 'for Office'}</div>
          {d.slogan && <div style={{ fontSize: '3.4cqw', color: '#333', fontStyle: 'italic', fontFamily: 'Georgia, serif', marginTop: '1.5cqw' }}>{d.slogan}</div>}
        </div>
      </div>
    </Frame>
  );
};

// ===========================================================================
// TEMPLATE 4 — "Grassroots" (hand-built, community, badge)
// ===========================================================================
const Grassroots = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg={CREAM}>
        <div style={{ ...cq, position: 'absolute', inset: 0, padding: '6cqw', color: '#222' }}>
          <div style={{ ...upper, fontWeight: 700, fontSize: '6cqw', color: c.primary }}>{d.slogan || 'Neighbors for a better tomorrow'}</div>
          <ul style={{ margin: '3cqw 0 0', padding: 0, listStyle: 'none', fontSize: '3.6cqw', lineHeight: 1.7, fontFamily: 'Georgia, serif' }}>
            <li>★ Lower taxes for working families</li>
            <li>★ Safer, stronger neighborhoods</li>
            <li>★ Better schools for every child</li>
          </ul>
          <PaidFor text={d.paidFor} color="rgba(0,0,0,.55)" />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg={c.primary}>
      <div style={{ ...cq, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* circular badge */}
        <div style={{ position: 'relative', width: '62%', aspectRatio: '1', borderRadius: '50%', background: CREAM, border: `1.6cqw solid ${GOLD}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <StarRow n={3} size={12} color={c.secondary} />
          <div style={{ ...upper, fontSize: '3cqw', letterSpacing: '.3em', color: c.secondary, fontWeight: 700, marginTop: '1cqw' }}>Elect</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '7.5cqw', lineHeight: .95, color: c.primary }}>{d.candidateName || 'Your Name'}</div>
          <div style={{ ...upper, fontSize: '3cqw', color: c.secondary, fontWeight: 700, marginTop: '1cqw' }}>{d.office || 'for Office'}</div>
        </div>
        <div style={{ position: 'absolute', bottom: '6%', ...upper, fontSize: '3.4cqw', color: '#fff', letterSpacing: '.3em' }}>{d.slogan || 'Together we rise'}</div>
        <PaidFor text={d.paidFor} />
      </div>
    </Frame>
  );
};

// ===========================================================================
// TEMPLATE 5 — "Flag Wave" (diagonal flag, dynamic)
// ===========================================================================
const FlagWave = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg="#fff">
        <div style={{ ...cq, position: 'absolute', inset: 0 }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '55%', height: '100%', background: `repeating-linear-gradient(180deg, ${c.primary} 0 14%, ${CREAM} 14% 28%)`, clipPath: 'polygon(20% 0, 100% 0, 100% 100%, 0 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, padding: '6cqw', width: '55%' }}>
            <div style={{ ...upper, fontWeight: 700, fontSize: '6cqw', color: c.primary, lineHeight: 1.05 }}>{d.slogan || 'Proud to serve our district'}</div>
            <div style={{ marginTop: '3cqw', fontSize: '3.3cqw', color: '#444', lineHeight: 1.4, fontFamily: 'Georgia, serif' }}>{d.message || 'Experienced, dedicated, and ready to fight for you on day one.'}</div>
          </div>
          <PaidFor text={d.paidFor} color="rgba(0,0,0,.55)" />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg={c.secondary}>
      <div style={{ ...cq, position: 'absolute', inset: 0 }}>
        <div style={{ position: 'absolute', inset: 0, background: c.primary, clipPath: 'polygon(0 0, 100% 0, 100% 62%, 0 100%)' }} />
        <div style={{ position: 'absolute', top: '10%', left: '6cqw', color: '#fff' }}>
          <StarRow n={5} size={11} />
          <div style={{ ...upper, fontSize: '3.4cqw', letterSpacing: '.35em', marginTop: '2cqw', opacity: .9 }}>Vote for</div>
          <div style={{ ...upper, fontWeight: 700, fontSize: '12cqw', lineHeight: .92, textShadow: '0 2px 8px rgba(0,0,0,.35)' }}>{d.candidateName || 'Your Name'}</div>
          <div style={{ ...upper, fontSize: '3.8cqw', color: GOLD, fontWeight: 700, marginTop: '1cqw' }}>{d.office || 'for Office'}</div>
        </div>
        <div style={{ position: 'absolute', bottom: '9%', right: '6cqw', ...upper, fontSize: '3.4cqw', color: '#fff', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{d.slogan || 'Leadership that delivers.'}</div>
        <PaidFor text={d.paidFor} />
      </div>
    </Frame>
  );
};

// ===========================================================================
// TEMPLATE 6 — "Ballot" (vote-mark / checkbox motif)
// ===========================================================================
const Ballot = ({ side, c, d }) => {
  if (side === 'back') {
    return (
      <Frame bg={CREAM}>
        <div style={{ ...cq, position: 'absolute', inset: 0, padding: '6cqw' }}>
          <div style={{ ...upper, fontWeight: 700, fontSize: '6cqw', color: c.primary }}>{d.slogan || 'Mark your ballot for real change'}</div>
          <div style={{ marginTop: '3cqw', fontSize: '3.4cqw', color: '#444', lineHeight: 1.4, fontFamily: 'Georgia, serif' }}>{d.message || 'Every vote counts. Make yours heard for a stronger, safer, more prosperous community.'}</div>
          <div style={{ position: 'absolute', bottom: '9%', left: '6cqw', display: 'flex', alignItems: 'center', gap: '2cqw' }}>
            <div style={{ width: '6cqw', height: '6cqw', border: `.8cqw solid ${c.primary}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.primary, fontWeight: 700, fontSize: '5cqw' }}>✓</div>
            <span style={{ ...upper, fontWeight: 700, fontSize: '3.6cqw', color: c.primary }}>{d.candidateName || 'Your Name'}</span>
          </div>
          <PaidFor text={d.paidFor} color="rgba(0,0,0,.55)" />
        </div>
      </Frame>
    );
  }
  return (
    <Frame bg="#fff">
      <div style={{ ...cq, position: 'absolute', inset: 0 }}>
        <div style={{ position: 'absolute', top: 0, width: '100%', height: '16%', background: c.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ ...upper, color: '#fff', letterSpacing: '.5em', fontSize: '3.4cqw', fontWeight: 700 }}>Official Ballot</span>
        </div>
        <div style={{ position: 'absolute', top: '22%', left: '8cqw', right: '8cqw' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4cqw' }}>
            <div style={{ width: '14cqw', height: '14cqw', border: `1.2cqw solid ${c.secondary}`, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.secondary, fontWeight: 700, fontSize: '11cqw' }}>✓</div>
            <div>
              <div style={{ ...upper, fontWeight: 700, fontSize: '9cqw', lineHeight: .95, color: '#111' }}>{d.candidateName || 'Your Name'}</div>
              <div style={{ ...upper, fontSize: '3.6cqw', color: c.secondary, fontWeight: 700 }}>{d.office || 'for Office'}</div>
            </div>
          </div>
          <div style={{ marginTop: '4cqw', ...upper, fontSize: '3.4cqw', color: c.primary, letterSpacing: '.2em', fontWeight: 700 }}>{d.slogan || '★ Vote by Nov 5 ★'}</div>
        </div>
        <PaidFor text={d.paidFor} color="rgba(0,0,0,.5)" />
      </div>
    </Frame>
  );
};

// ---- registry --------------------------------------------------------------
const RENDERERS = { stars: Stars, modern: Modern, bold: BoldType, grassroots: Grassroots, flag: FlagWave, ballot: Ballot };

export const TEMPLATES = [
  { id: 'stars',      label: 'Stars & Stripes' },
  { id: 'modern',     label: 'Modern Civic' },
  { id: 'bold',       label: 'Bold Type' },
  { id: 'grassroots', label: 'Grassroots' },
  { id: 'flag',       label: 'Flag Wave' },
  { id: 'ballot',     label: 'Official Ballot' },
];

/** Render one side of a postcard template, live from `data`. */
export function PostcardPreview({ templateId, side = 'front', data = {} }) {
  const R = RENDERERS[templateId] || Stars;
  const c = partyColors(data.party);
  return <R side={side} c={c} d={data} />;
}

export default PostcardPreview;
